import { Injectable } from '@angular/core';
import { BrandFilterKey, BrandVisibilityControl } from '../../../../../models/enum/shared/brand.enum';
import { exists } from '../../../../../functions/exists';
import { BrandFilterState } from '../brands-filter-index';

export interface BrandsFiltersUiState {
  readonly searchText: string;
  readonly selectedFilters: readonly BrandFilterKey[];
  readonly filters: ReadonlyMap<BrandFilterKey, string[] | string>;
  readonly visibilityState: ReadonlyMap<BrandVisibilityControl, boolean>;
}

// Outlives the component-scoped brands view models so the table keeps its search and filters across navigation,
// matching ProductGroupsFiltersStateService. It is in memory only, so a refresh starts clean.
@Injectable({ providedIn: 'root' })
export class BrandsFiltersStateService {
  private state: BrandsFiltersUiState = {
    searchText: '',
    selectedFilters: [],
    filters: new Map(),
    visibilityState: BrandsFiltersStateService.createAllVisibleState()
  };

  static createAllVisibleState(): Map<BrandVisibilityControl, boolean> {
    return new Map(Object.values(BrandVisibilityControl).map(key => [key, true]));
  }

  snapshot(): BrandsFiltersUiState {
    return {
      searchText: this.state.searchText,
      selectedFilters: [...this.state.selectedFilters],
      filters: new Map(this.state.filters),
      visibilityState: new Map(this.state.visibilityState)
    };
  }

  updateSearchText(searchText: string | null | undefined): void {
    this.state = { ...this.state, searchText: searchText ?? '' };
  }

  updateSelectedFilters(selectedFilters: readonly BrandFilterKey[]): void {
    this.state = { ...this.state, selectedFilters: [...(selectedFilters ?? [])] };
  }

  updateFilters(filters: ReadonlyMap<BrandFilterKey, string[] | string>): void {
    this.state = { ...this.state, filters: new Map(filters ?? []) };
  }

  updateVisibilityState(visibilityState: ReadonlyMap<BrandVisibilityControl, boolean>): void {
    this.state = { ...this.state, visibilityState: new Map(visibilityState ?? []) };
  }

  toFilterState(state: BrandsFiltersUiState = this.snapshot()): BrandFilterState {
    const selectedFilterKeys = new Set(state?.selectedFilters ?? []);
    const selectedFilters = new Map<BrandFilterKey, readonly string[]>();
    state?.filters?.forEach((value, key) => {
      const values = this.getNonEmptyFilterValues(value);
      if (selectedFilterKeys.has(key) && values.length > 0) selectedFilters.set(key, values);
    });
    const visibilityControls = new Set<BrandVisibilityControl>();
    state?.visibilityState?.forEach((isActive, key) => {
      if (!isActive) visibilityControls.add(key);
    });
    return { selectedFilters, visibilityControls, searchText: state?.searchText ?? '' };
  }

  private getNonEmptyFilterValues(value: string[] | string | null | undefined): string[] {
    const values = Array.isArray(value) ? value : [value];
    return values.map(item => item?.trim()).filter(item => exists(item) && item.length > 0);
  }
}
